import { ref, reactive, computed } from 'vue'
import { MaybeRef, useEventBus } from '@vueuse/core'
import {
  ContentToolbarPropsType,
  OptionsKeysType,
  ContentToolbarEvtType
} from './props'
import { NavViewMode } from '@/store/navigator'
import {
  isOtl,
  isDoc,
  isPpt,
  isXls,
  isKsheet,
  isPom,
  isPdf,
  isOfd
} from '@teams/common/hooks/useFileList/fileConfig'

const handleOrderByExt = (
  ext: string,
  canEdit: boolean
): Array<OptionsKeysType> => {
  // 根据后缀名决定工具栏的返回
  const handlerMap: Array<{
    condition: (...args: unknown[]) => boolean
    handle: (...args: unknown[]) => Array<OptionsKeysType>
  }> = [
    {
      condition: () => isOtl(ext),
      handle: () => {
        if (canEdit) {
          return ['edit', 'directory']
        } else {
          return ['directory']
        }
      }
    },
    {
      condition: () =>
        isDoc(ext) || isPpt(ext) || isPom(ext) || isXls(ext) || isKsheet(ext),
      handle: () => {
        if (canEdit) {
          return ['edit']
        } else {
          return []
        }
      }
    }
  ]
  for (const handler of handlerMap) {
    const { condition, handle } = handler
    if (condition()) {
      return handle()
    }
  }
  // default
  return []
}

const handleShowEditBtnByViewMode = (
  mode: NavViewMode,
  ext: string,
  canEdit: boolean
): boolean => {
  // 特定后缀名的编辑模式下开启【完成编辑】按钮
  const handlerMap: Array<{
    condition: (...args: unknown[]) => boolean
    handle: (...args: unknown[]) => boolean
  }> = [
    {
      condition: () =>
        isDoc(ext) || isPpt(ext) || isOtl(ext) || isXls(ext) || isKsheet(ext),
      handle: () => canEdit && mode === 'Write'
    }
  ]
  for (const handler of handlerMap) {
    const { condition, handle } = handler
    if (condition()) {
      return handle()
    }
  }
  // default
  return false
}
const handleShowToolBarByViewMode = (mode: NavViewMode): boolean => {
  // 阅读模式下开启工具栏
  return mode === 'Read'
}

const handleBtnPosition: (
  ext: string,
  mode: NavViewMode
) => {
  top: string
  right: string
} = (ext, mode) => {
  const handlerMap: Array<{
    condition: (...args: unknown[]) => boolean
    handle: (...args: unknown[]) => {
      top: string
      right: string
    }
  }> = [
    {
      condition: () => isOtl(ext),
      handle: () => {
        if (mode === 'Read') {
          return {
            top: '16px',
            right: '16px'
          }
        }
        return {
          top: '76px',
          right: '16px'
        }
      }
    },
    {
      condition: () => isDoc(ext),
      handle: () => {
        if (mode === 'Read') {
          return {
            top: '16px',
            right: '16px'
          }
        }
        return {
          top: '116px',
          right: '16px'
        }
      }
    },
    {
      condition: () => isXls(ext) || isKsheet(ext),
      handle: () => {
        if (mode === 'Read') {
          return {
            top: '64px',
            right: '24px'
          }
        }
        return {
          top: '156px',
          right: '24px'
        }
      }
    },
    {
      condition: () => isPpt(ext),
      handle: () => {
        if (mode === 'Read') {
          return {
            top: '16px',
            right: '16px'
          }
        }
        return {
          top: '116px',
          right: '16px'
        }
      }
    },
    {
      condition: () => isPdf(ext),
      handle: () => ({
        top: '124px',
        right: '16px'
      })
    },
    {
      condition: () => isOfd(ext),
      handle: () => ({
        top: '270px',
        right: '16px'
      })
    }
  ]
  for (const handler of handlerMap) {
    const { condition, handle } = handler
    if (condition()) {
      return handle()
    }
  }
  // default
  console.log('当前文件:', ext, `${mode} 模式`)
  return {
    top: '0px',
    right: '0px'
  }
}

export const useContentToolbar = (
  options: {
    ext: MaybeRef<string>
    viewMode: MaybeRef<NavViewMode>
    canEdit: MaybeRef<boolean>
  },
  overrides: Partial<{
    [key in keyof ContentToolbarPropsType]: MaybeRef<
      ContentToolbarPropsType[key]
    >
  }> = {}
) => {
  const evtBus = useEventBus<ContentToolbarEvtType>('contentToolbar')
  const ext = ref(options.ext)
  const viewMode = ref(options.viewMode)
  const canEdit = ref(options.canEdit)
  const optionsOrder = computed<Array<OptionsKeysType>>(() =>
    handleOrderByExt(ext.value, canEdit.value)
  )
  const btnPosition = computed(() =>
    handleBtnPosition(ext.value, viewMode.value)
  )
  const props = ref({
    optionsOrder,
    showToolbarBtn: computed(
      // 阅读模式下且工具栏数目不为 0 的前提下开启工具栏
      () =>
        handleShowToolBarByViewMode(viewMode.value) &&
        optionsOrder.value.length > 0
    ),
    showEditBtn: computed(() =>
      handleShowEditBtnByViewMode(viewMode.value, ext.value, canEdit.value)
    ),
    top: computed(() => btnPosition.value.top),
    right: computed(() => btnPosition.value.right),
    evtBus,
    ...overrides
  })
  return reactive({
    props,
    evtBus
  })
}
